import AuthClientStore from "../services/auth-client.service";
import { routes } from '../../assets/constants';
import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';
import {
    CsAsset,
    CsAssetsListResponse,
    CsBrandKit,
    CsCreditEntry,
    CsGenerationCreateResponse,
    CsGenerationJob,
    CsProject,
    CsProjectsListResponse,
    CsRecipe,
    CreateCsGenerationPayload,
    CreateCsProjectPayload,
    ExtractBrandIdentityPayload,
    UpdateCsBrandKitPayload,
    UpdateCsProjectPayload,
    CsAgentRun,
    CsAgentRunStreamEvent,
    CsAgentThread,
} from '../models/content-studio.model';

/**
 * Content Studio API hook — mirrors the pms-api-v2 controller.
 * Uses `sendAuthGuardedRequest` so 401s auto-refresh the access token.
 */
export const useContentStudioApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    // ── 3D-studio prop library ──────────────────────────────────
    // Global and read-only: every vendor browses the same shelf. The list is
    // picker metadata; the GLB itself comes per prop from getStudioPropModel,
    // which is where the server decrypts it.

    const listStudioProps = async (category?: string, scope?: 'shared' | 'mine' | 'all'): Promise<StudioProp[]> => {
        const params = [
            category ? `category=${encodeURIComponent(category)}` : '',
            scope ? `scope=${scope}` : '',
        ].filter(Boolean).join('&');
        const suffix = params ? `?${params}` : '';
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.listStudioProps + suffix,
        );
        return response?.data || response || [];
    };

    /** Queue a prop from the vendor's own photograph. Returns while it generates. */
    const createStudioProp = async (
        file: File,
        input: { name?: string; category?: string } = {},
    ): Promise<{ propId: string; jobId: string; status: string }> => {
        const data = new FormData();
        data.append('image', file);
        if (input.name) data.append('name', input.name);
        if (input.category) data.append('category', input.category);
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST, routes.contentStudio.createStudioProp, data,
        );
        return response?.data || response;
    };

    /**
     * Poll a generating prop. This is also what finalises it: the server copies
     * the finished GLB into the library on the first poll that finds the job
     * done, before the job's own copy expires.
     */
    const getStudioPropStatus = async (propId: string): Promise<{
        propId: string; status: 'ready' | 'generating' | 'failed'; previewUrl?: string; error?: string;
    }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET, routes.contentStudio.studioPropStatus(propId),
        );
        return response?.data || response;
    };

    const deleteStudioProp = async (propId: string): Promise<void> => {
        await sendAuthGuardedRequest(ApiMethod.DELETE, routes.contentStudio.deleteStudioProp(propId));
    };

    const listStudioPropCategories = async (): Promise<string[]> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.listStudioPropCategories,
        );
        return response?.data || response || [];
    };

    // ───────────────────────────────────────────────────────────
    // Projects
    // ───────────────────────────────────────────────────────────

    const listProjects = async (params?: {
        search?: string;
        page?: number;
        limit?: number;
    }): Promise<CsProjectsListResponse> => {
        const qs = new URLSearchParams();
        if (params?.search) qs.set('search', params.search);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.listProjects + suffix,
        );
        return response?.data || response;
    };

    const getProject = async (uuid: string): Promise<CsProject> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.getProject(uuid),
        );
        return response?.data || response;
    };

    const createProject = async (payload: CreateCsProjectPayload): Promise<CsProject> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.createProject,
            payload,
        );
        return response?.data || response;
    };

    const updateProject = async (
        uuid: string,
        payload: UpdateCsProjectPayload,
    ): Promise<CsProject> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.contentStudio.updateProject(uuid),
            payload,
        );
        return response?.data || response;
    };

    /**
     * Cut the background out of a moodboard image.
     *
     * Server-side because it is a paid model call: the key stays on the API and
     * the result is stored on our bucket, so a board keeps working after the
     * generator drops its own copy.
     */
    const cutoutImage = async (imageUrl: string): Promise<string> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.cutoutImage,
            { imageUrl },
        );
        return (response?.data || response)?.url;
    };

    const deleteProject = async (uuid: string): Promise<{ success: boolean }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.DELETE,
            routes.contentStudio.deleteProject(uuid),
        );
        return response?.data || response;
    };

    // ───────────────────────────────────────────────────────────
    // Assets
    // ───────────────────────────────────────────────────────────

    const listAssets = async (params?: {
        projectUuid?: string;
        productUuid?: string;
        kind?: string;
        status?: string;
        page?: number;
        limit?: number;
    }): Promise<CsAssetsListResponse> => {
        const qs = new URLSearchParams();
        if (params?.projectUuid) qs.set('projectUuid', params.projectUuid);
        if (params?.productUuid) qs.set('productUuid', params.productUuid);
        if (params?.kind) qs.set('kind', params.kind);
        if (params?.status) qs.set('status', params.status);
        if (params?.page) qs.set('page', String(params.page));
        if (params?.limit) qs.set('limit', String(params.limit));
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.listAssets + suffix,
        );
        return response?.data || response;
    };

    const getAsset = async (uuid: string): Promise<CsAsset> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.getAsset(uuid),
        );
        return response?.data || response;
    };

    const updateAssetPosition = async (
        uuid: string,
        canvasX: number,
        canvasY: number,
    ): Promise<CsAsset> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.contentStudio.updateAssetPosition(uuid),
            { canvasX, canvasY },
        );
        return response?.data || response;
    };

    const updateAssetNote = async (uuid: string, note: string): Promise<CsAsset> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.contentStudio.updateAssetNote(uuid),
            { note },
        );
        return response?.data || response;
    };

    const deleteAsset = async (uuid: string): Promise<{ success: boolean }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.DELETE,
            routes.contentStudio.deleteAsset(uuid),
        );
        return response?.data || response;
    };

    // ───────────────────────────────────────────────────────────
    // Generations
    // ───────────────────────────────────────────────────────────

    const createGeneration = async (
        payload: CreateCsGenerationPayload,
    ): Promise<CsGenerationCreateResponse> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.createGeneration,
            payload,
        );
        return response?.data || response;
    };

    const getGeneration = async (jobId: string): Promise<CsGenerationJob> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.getGeneration(jobId),
        );
        return response?.data || response;
    };

    const listRecentGenerations = async (): Promise<CsGenerationJob[]> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.listGenerations,
        );
        return response?.data || response || [];
    };

    const cancelGeneration = async (jobId: string): Promise<CsGenerationJob> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.cancelGeneration(jobId),
        );
        return response?.data || response;
    };

    // ───────────────────────────────────────────────────────────
    // Recipes
    // ───────────────────────────────────────────────────────────

    const listRecipes = async (): Promise<CsRecipe[]> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.listRecipes,
        );
        return response?.data || response || [];
    };

    // ───────────────────────────────────────────────────────────
    // Brand Kit
    // ───────────────────────────────────────────────────────────

    const getBrandKit = async (): Promise<CsBrandKit> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.getBrandKit,
        );
        return response?.data || response;
    };

    const updateBrandKit = async (
        payload: UpdateCsBrandKitPayload,
    ): Promise<CsBrandKit> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.PATCH,
            routes.contentStudio.updateBrandKit,
            payload,
        );
        return response?.data || response;
    };

    /**
     * Work the brand out from a website and public profiles, streamed.
     *
     * Same wire shape as the extract stream, but the reading is real: pages
     * are fetched, the palette comes from CSS, and the final tool_result
     * carries the kit plus a report of what was read and what could not be.
     */
    /**
     * Read a brand SSE stream. Shared by the two endpoints that produce one,
     * because the parsing is identical and a second copy of it is a second
     * place for the `[DONE]` handling to drift.
     */
    const brandStream = (
        route: string,
        payload: Record<string, unknown>,
        onEvent: (event: { type: string; data: any }) => void,
    ): AbortController => {
        const controller = new AbortController();
        (async () => {
            try {
                const token = AuthClientStore.getAccessToken();
                const baseUrl = process.env.REACT_APP_PMS_API_URL || 'http://localhost:8082';
                const response = await fetch(`${baseUrl}${route}`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                    signal: controller.signal,
                });
                if (!response.body) throw new Error('No response body');
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        const raw = line.slice(6).trim();
                        if (raw === '[DONE]') { onEvent({ type: 'done', data: {} }); return; }
                        try { onEvent(JSON.parse(raw)); } catch { /* malformed chunk */ }
                    }
                }
            } catch (err: any) {
                if (err.name !== 'AbortError') onEvent({ type: 'error', data: { message: err.message } });
            }
        })();
        return controller;
    };

    const discoverBrandStream = (
        payload: {
            websiteUrl?: string; instagram?: string; facebook?: string; pinterest?: string;
            keep?: string[];
            /** 'social' reads only the connected accounts — a shorter, cheaper run. */
            only?: 'social';
        },
        onEvent: (event: { type: string; data: any }) => void,
    ): AbortController => brandStream(routes.contentStudio.discoverBrand, payload, onEvent);

    /**
     * Look at the reference pictures again, and nothing else.
     *
     * The visual style is read from those pictures, so changing them leaves the
     * prose describing photographs that may not be there. This is one vision
     * pass over the current set — not a re-read of the website, which is what
     * "Read again" would cost to repair a single derived field.
     */
    const lookAgainStream = (
        onEvent: (event: { type: string; data: any }) => void,
    ): AbortController => brandStream(routes.contentStudio.lookAgain, {}, onEvent);

    /**
     * Extract brand identity via SSE stream. Returns an AbortController so
     * the caller can cancel mid-stream, and emits parsed events through
     * the `onEvent` callback. Mirrors the pattern used by the categories
     * AI agent button.
     */
    const extractBrandIdentityStream = (
        payload: ExtractBrandIdentityPayload,
        onEvent: (event: { type: string; data: any }) => void,
    ): AbortController => {
        const controller = new AbortController();

        (async () => {
            try {
                const token = AuthClientStore.getAccessToken();
                const baseUrl = process.env.REACT_APP_PMS_API_URL || 'http://localhost:8082';
                const response = await fetch(
                    `${baseUrl}${routes.contentStudio.extractBrandIdentity}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(payload),
                        signal: controller.signal,
                    },
                );

                if (!response.body) throw new Error('No response body');
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });

                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        const payload = line.slice(6).trim();
                        if (payload === '[DONE]') {
                            onEvent({ type: 'done', data: {} });
                            return;
                        }
                        try {
                            const parsed = JSON.parse(payload);
                            onEvent(parsed);
                        } catch {
                            // Ignore malformed chunks
                        }
                    }
                }
            } catch (err: any) {
                if (err.name !== 'AbortError') {
                    onEvent({ type: 'error', data: { message: err.message } });
                }
            }
        })();

        return controller;
    };

    // ───────────────────────────────────────────────────────────
    // Agent runs
    // ───────────────────────────────────────────────────────────

    const createAgentRun = async (payload: {
        profile: string;
        message: string;
        projectUuid?: string;
        /** Continue a conversation. Omit on the first message. */
        threadId?: string;
        /** Let the model reason before answering. Costs tokens and latency. */
        thinking?: boolean;
        context?: Record<string, any>;
        autonomy?: 'suggest' | 'approved';
    }): Promise<CsAgentRun> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.createAgentRun,
            payload,
        );
        return response?.data || response;
    };

    const getAgentRun = async (runId: string): Promise<CsAgentRun> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.getAgentRun(runId),
        );
        return response?.data || response;
    };

    /**
     * Stop a run.
     *
     * Sets a terminal status; the executor re-reads that between iterations
     * and stops at the next turn rather than mid-call, so nothing is paid for
     * and then thrown away. Charges already made stand — the work was really
     * done — while a run that FAILS is refunded in full.
     */
    const cancelAgentRun = async (runId: string): Promise<CsAgentRun> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.cancelAgentRun(runId),
        );
        return response?.data || response;
    };

    /**
     * Subscribe to a run's event stream.
     *
     * The run is already executing server-side — this only watches it, so
     * aborting stops the watching, not the work. Pass `after` to resume from a
     * sequence number after a dropped connection instead of replaying from the
     * beginning.
     *
     * Returns an AbortController so the caller can detach on unmount.
     */
    const streamAgentRun = (
        runId: string,
        onEvent: (event: CsAgentRunStreamEvent) => void,
        options: { after?: number } = {},
    ): AbortController => {
        const controller = new AbortController();

        (async () => {
            try {
                const token = AuthClientStore.getAccessToken();
                const baseUrl = process.env.REACT_APP_PMS_API_URL || 'http://localhost:8082';
                const suffix = options.after != null ? `?after=${options.after}` : '';
                const response = await fetch(
                    `${baseUrl}${routes.contentStudio.streamAgentRun(runId)}${suffix}`,
                    {
                        method: 'GET',
                        headers: { Authorization: `Bearer ${token}` },
                        signal: controller.signal,
                    },
                );

                if (!response.body) throw new Error('No response body');
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });

                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        const raw = line.slice(6).trim();
                        if (raw === '[DONE]') {
                            onEvent({ type: 'done', data: {} });
                            return;
                        }
                        try {
                            onEvent(JSON.parse(raw));
                        } catch {
                            // Ignore malformed chunks
                        }
                    }
                }
            } catch (err: any) {
                if (err.name !== 'AbortError') {
                    onEvent({ type: 'error', data: { message: err.message } });
                }
            }
        })();

        return controller;
    };

    const rateAgentRun = async (
        runId: string,
        feedback: 'up' | 'down' | null,
        note?: string,
    ): Promise<CsAgentRun> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.rateAgentRun(runId),
            { feedback, note },
        );
        return response?.data || response;
    };

    const markAgentRunApplied = async (runId: string): Promise<{ success: boolean }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.markAgentRunApplied(runId),
        );
        return response?.data || response;
    };

    /** The vendor approves a step the agent proposed (a publish, a spend); the recorded call runs now. */
    const approveAgentStep = async (runId: string, index: number): Promise<{ applied: boolean; tool: string; result?: unknown }> => {
        const response: any = await sendAuthGuardedRequest(ApiMethod.POST, routes.contentStudio.approveAgentStep(runId, index), {});
        return response?.data || response;
    };

    const declineAgentStep = async (runId: string, index: number): Promise<void> => {
        await sendAuthGuardedRequest(ApiMethod.POST, routes.contentStudio.declineAgentStep(runId, index), {});
    };

    /**
     * Continue a run that paused because it had spent its credit allowance.
     *
     * It resumes from the conversation it had already built, so this costs
     * what is left of the job rather than the whole job again.
     */
    const resumeAgentRun = async (runId: string): Promise<CsAgentRun> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST, routes.contentStudio.resumeAgentRun(runId), {},
        );
        return response?.data || response;
    };

    /** Recent runs across every profile — the Work page's source. */
    const listAgentRuns = async (
        opts: { profile?: string; status?: string; limit?: number } = {},
    ): Promise<CsAgentRun[]> => {
        const params = new URLSearchParams();
        if (opts.profile) params.set('profile', opts.profile);
        if (opts.status) params.set('status', opts.status);
        if (opts.limit) params.set('limit', String(opts.limit));
        const query = params.toString();
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.listAgentRuns + (query ? `?${query}` : ''),
        );
        return response?.data || response || [];
    };

    const listAgentThreads = async (projectUuid?: string): Promise<CsAgentThread[]> => {
        const suffix = projectUuid ? `?projectUuid=${encodeURIComponent(projectUuid)}` : '';
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.listAgentThreads + suffix,
        );
        return response?.data || response || [];
    };

    const getAgentThread = async (
        threadId: string,
    ): Promise<{ thread: CsAgentThread; runs: CsAgentRun[] }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.getAgentThread(threadId),
        );
        return response?.data || response;
    };

    const closeAgentThread = async (threadId: string): Promise<{ success: boolean }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.closeAgentThread(threadId),
        );
        return response?.data || response;
    };

    /**
     * Promise to come back once this plan's images exist.
     *
     * The server watches the project's assets and starts the follow-up itself.
     * This used to be a localStorage key and an effect in the panel, which made
     * it a promise only the tab that armed it could keep.
     */
    const armAgentContinuation = async (payload: {
        projectUuid: string;
        threadId: string;
        instruction: string;
        expectedAssets: number;
        canvasSnapshot?: Record<string, any>;
    }): Promise<{ continuationId: string }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.agentContinuations,
            payload,
        );
        return response?.data || response;
    };

    const listAgentContinuations = async (projectUuid: string): Promise<any[]> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            `${routes.contentStudio.agentContinuations}?projectUuid=${encodeURIComponent(projectUuid)}`,
        );
        return response?.data || response || [];
    };

    const cancelAgentContinuation = async (continuationId: string): Promise<any> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.DELETE,
            routes.contentStudio.cancelAgentContinuation(continuationId),
        );
        return response?.data || response;
    };

    // ───────────────────────────────────────────────────────────
    // Credits
    // ───────────────────────────────────────────────────────────

    const getCreditBalance = async (): Promise<{ balance: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.creditsBalance,
        );
        return response?.data || response;
    };

    const getCreditHistory = async (): Promise<CsCreditEntry[]> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.creditsHistory,
        );
        return response?.data || response || [];
    };

    // ─── Super-admin: per-vendor credit inspection + adjustment ──────
    // The two endpoints are guarded by superadmin-strategy on the API
    // side; calling them with a non-super-admin token will 401.
    const adminGetCreditsForVendor = async (
        vendorId: string,
    ): Promise<{ vendorId: string; balance: number; history: CsCreditEntry[] }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.adminCreditsForVendor(vendorId),
        );
        return response?.data || response;
    };

    const adminSetCreditsForVendor = async (
        vendorId: string,
        targetBalance: number,
        reason?: string,
    ): Promise<{ vendorId: string; previousBalance: number; newBalance: number; delta: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.adminSetCreditsForVendor(vendorId),
            { targetBalance, reason },
        );
        return response?.data || response;
    };

    const uploadStyleRef = async (file: File): Promise<{ url: string }> => {
        const formData = new FormData();
        formData.append('file', file);
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.uploadStyleRef,
            formData,
        );
        return response?.data || response;
    };

    /**
     * Inspiration by address. The vendor is already looking at the picture
     * somewhere else; this saves them downloading it first.
     *
     * The server fetches and stores it rather than keeping the link: remote
     * addresses expire, hotlink-block, and cannot be handed to a model.
     */
    const inspirationFromUrl = async (url: string): Promise<{ url: string; sourceUrl: string }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.inspirationFromUrl,
            { url },
        );
        return response?.data || response;
    };

    // From Reference — detect the replaceable objects in the uploaded style image
    // so the vendor can pick the target object from a list (YOLO-World).
    const detectReferenceObjects = async (
        imageUrl: string,
    ): Promise<{ objects: Array<{ label: string; confidence: number; areaPct: number; bbox: number[] }> }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.detectReferenceObjects,
            { imageUrl },
        );
        return response?.data || response;
    };

    /**
     * Recover the camera a reference photograph was taken with.
     *
     * Returns `cameraView` already serialised — the studio poses to the whole
     * measurement rather than to whichever fields somebody remembered to copy.
     */
    const measureReferenceCamera = async (
        imageUrl: string,
    ): Promise<{ measured: boolean; cameraView?: string; camera?: any }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.measureReferenceCamera,
            { imageUrl },
        );
        return response?.data || response;
    };

    // ── Place in a room: the chosen room, analysed like a visualizer Space ──

    /** Queue a room image for analysis. The spaceId is known at once; the scene is not. */
    const analyzeRoom = async (imageUrl: string): Promise<{ jobId: string; spaceId: string }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.analyzeRoom,
            { imageUrl },
        );
        return response?.data || response;
    };

    const getRoomAnalysis = async (
        jobId: string,
    ): Promise<{ status: string; progress?: number; message?: string; error?: string; spaceId?: string }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.roomAnalysisStatus(jobId),
        );
        return response?.data || response;
    };

    /** The analysed room as the visualizer reads it — decrypted server-side. */
    const getRoomScene = async (spaceId: string): Promise<any> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.GET,
            routes.contentStudio.roomScene(spaceId),
        );
        return response?.data || response;
    };

    /**
     * Bring an image onto the canvas as a source — a file, or a link the server
     * fetches. Comes back as a ready asset, like any generated image.
     */
    const importImage = async (
        projectUuid: string,
        source: { file: File } | { url: string },
    ): Promise<CsAsset> => {
        let body: any;
        if ('file' in source) {
            body = new FormData();
            body.append('file', source.file);
        } else {
            body = { url: source.url };
        }
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.importImage(projectUuid),
            body,
        );
        return response?.data || response;
    };

    /**
     * Bring a finished video in as a ready asset — the timeline editor's
     * export, or a clip uploaded into the editor. The browser already knows
     * its size and length, so they travel with it; the poster is optional.
     */
    const importVideo = async (
        projectUuid: string,
        file: File,
        meta: { width?: number; height?: number; durationSec?: number; thumbnail?: Blob | null; origin?: 'editor' | 'upload' } = {},
    ): Promise<CsAsset> => {
        const body = new FormData();
        body.append('file', file);
        if (meta.thumbnail) body.append('thumbnail', new File([meta.thumbnail], 'thumbnail.png', { type: meta.thumbnail.type || 'image/png' }));
        if (meta.width) body.append('width', String(Math.round(meta.width)));
        if (meta.height) body.append('height', String(Math.round(meta.height)));
        if (meta.durationSec) body.append('durationSec', String(meta.durationSec));
        if (meta.origin) body.append('origin', meta.origin);
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.importImage(projectUuid),
            body,
        );
        return response?.data || response;
    };

    /**
     * Write a video storyboard from a brief: scenes the studio can shoot, how
     * each moves, captions, music. Returns the API's body as-is (the caller
     * reads the storyboard out of it — see video-story.ts readStoryboard).
     */
    const createStoryboard = async (input: {
        brief: string;
        productUuids: string[];
        aspect?: string;
        targetSec?: number;
        inspirationFrames?: string[];
        language?: string;
    }): Promise<any> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.storyboard,
            input,
        );
        return response?.data || response;
    };

    /** A music bed for the timeline editor. Resolves to a playable URL. */
    const generateMusic = async (input: { prompt: string; durationSec: number }): Promise<{ url: string; durationSec?: number }> => {
        const response: any = await sendAuthGuardedRequest(
            ApiMethod.POST,
            routes.contentStudio.generateMusic,
            input,
        );
        const data = response?.data || response;
        if (!data?.url) throw new Error('No music was returned');
        return data;
    };

    return {
        importImage,
        importVideo,
        generateMusic,
        createStoryboard,
        analyzeRoom,
        getRoomAnalysis,
        getRoomScene,
        // 3D-studio prop library
        listStudioProps,
        listStudioPropCategories,
        createStudioProp,
        getStudioPropStatus,
        deleteStudioProp,
        // Projects
        listProjects,
        getProject,
        createProject,
        updateProject,
        cutoutImage,
        deleteProject,
        // Assets
        listAssets,
        getAsset,
        updateAssetPosition,
        updateAssetNote,
        deleteAsset,
        // Generations
        createGeneration,
        getGeneration,
        listRecentGenerations,
        cancelGeneration,
        // Recipes
        listRecipes,
        // Brand Kit
        getBrandKit,
        updateBrandKit,
        extractBrandIdentityStream,
        discoverBrandStream,
        lookAgainStream,
        // Credits
        getCreditBalance,
        getCreditHistory,
        // Super-admin credits
        adminGetCreditsForVendor,
        adminSetCreditsForVendor,
        // Style reference
        uploadStyleRef,
        inspirationFromUrl,
        detectReferenceObjects,
        measureReferenceCamera,
        // Agent runs
        createAgentRun,
        getAgentRun,
        cancelAgentRun,
        streamAgentRun,
        rateAgentRun,
        markAgentRunApplied,
        // Agent threads
        listAgentRuns,
        resumeAgentRun,
        approveAgentStep,
        declineAgentStep,
        listAgentThreads,
        getAgentThread,
        closeAgentThread,
        // Agent continuations
        armAgentContinuation,
        listAgentContinuations,
        cancelAgentContinuation,
    };
};

export interface StudioProp {
    propId: string;
    name: string;
    category: string;
    previewUrl?: string;
    /** `generating` while the worker is making the mesh; `failed` if it could not. */
    status?: 'ready' | 'generating' | 'failed';
    error?: string;
    /** True for a prop this vendor generated, false for the shared shelf. */
    mine?: boolean;
}

/**
 * The prop's GLB as an object URL the studio can hand to its loader.
 *
 * Mirrors `getProductModelObjectUrl`: the stored file is ciphertext and only
 * the auth-gated endpoint returns plaintext, so a plain <url> can never work.
 * Cached per prop for the session — a vendor placing three of the same chair
 * should download it once.
 */
const propModelCache = new Map<string, Promise<string>>();

export async function getStudioPropModelObjectUrl(propId: string): Promise<string> {
    const cached = propModelCache.get(propId);
    if (cached) return cached;

    const promise = (async () => {
        const authToken = AuthClientStore.getAccessToken();
        const base = process.env.REACT_APP_PMS_API_URL || 'http://localhost:8082';
        const response = await fetch(`${base}${routes.contentStudio.studioPropModel(propId)}`, {
            headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!response.ok) throw new Error(`Prop model ${propId}: HTTP ${response.status}`);
        const buffer = await response.arrayBuffer();
        const magic = new TextDecoder().decode(new Uint8Array(buffer, 0, Math.min(4, buffer.byteLength)));
        if (magic !== 'glTF') throw new Error(`Prop model ${propId} is not a GLB`);
        return URL.createObjectURL(new Blob([buffer], { type: 'model/gltf-binary' }));
    })();

    // A failed download must not poison the cache — the next click retries.
    promise.catch(() => propModelCache.delete(propId));
    propModelCache.set(propId, promise);
    return promise;
}
